import { EmptyState } from "@/design-system/components/ui";
import { useUserPreference } from "@/hooks/preferences/useUserPreference";
import type { BuyerScope } from "./buyerFilters";

/**
 * Addendum B (B5) — the firm-wide Submissions and Buyers pages open on "My"
 * by default and then on whichever scope the reader chose last.
 *
 * <p>Where the choice lives: the per-user preference store
 * (<code>api/me/preferences</code>, <code>StaffUserPreferences</code>), keyed on
 * the server by the acting user's id — the impersonated user's under
 * impersonation, and starting or ending impersonation reloads the app. One key
 * per page. The browser reads it once per app load (<code>staleTime:
 * Infinity</code>, a cache key with no user in it), so signing out and in as
 * someone else in the same tab shows the previous user's choice until a reload
 * — as the saved view and column choices already do.</p>
 */
export const SUBMISSIONS_SCOPE_PREFERENCE = "submissions-list-scope";
export const BUYERS_SCOPE_PREFERENCE = "buyers-list-scope";

/**
 * URL → remembered choice → "mine". A URL value wins (a link opens on what it
 * says); anything else that is not one of the two scopes — an unset or
 * unreadable preference, a failed read — counts as no choice.
 */
export function resolveListScope(fromUrl: unknown, remembered: unknown): BuyerScope {
  if (fromUrl === "mine" || fromUrl === "all") return fromUrl;
  if (remembered === "mine" || remembered === "all") return remembered;
  return "mine";
}

/**
 * The effective scope for a list page, and the one way to change it: the
 * choice goes into the URL (so the view is shareable) AND is remembered.
 * `isLoading` holds the page's list query until the remembered choice is known,
 * so it does not fetch "mine" and then "all" one render apart.
 */
export function useRememberedScope(
  preferenceKey: string,
  fromUrl: BuyerScope | "",
  writeUrl: (next: BuyerScope) => void,
): { scope: BuyerScope; choose: (next: BuyerScope) => void; isLoading: boolean } {
  const remembered = useUserPreference<string>(preferenceKey, "");
  return {
    scope: resolveListScope(fromUrl, remembered.value),
    choose: (next) => {
      writeUrl(next);
      remembered.set(next);
    },
    isLoading: remembered.isLoading,
  };
}

/**
 * The empty "My" view, with SHOW ALL (B5).
 *
 * <p>🔴 "You have no … yet" is said only when it is TRUE — when the reader's
 * whole "My" book is empty (<code>nothingYet</code>, which the host learns from
 * a one-row probe without its other filters). A "My" view narrowed by a search,
 * a status or a view to nothing says that nothing matches instead.</p>
 */
export function MyScopeEmptyState({
  noun,
  nothingYet,
  onShowAll,
}: {
  noun: "buyers" | "submissions";
  nothingYet: boolean;
  onShowAll: () => void;
}) {
  const yours = noun === "buyers" ? "Buyers assigned to you" : "Submissions for the buyers you own";
  return (
    <EmptyState
      title={nothingYet ? `You have no ${noun} yet` : `None of your ${noun} match`}
      description={
        nothingYet
          ? `${yours} appear here. Show all to see everyone's.`
          : "Widen the filters, or show everyone's."
      }
      variant={nothingYet ? "empty" : "noResults"}
      action={{ label: "Show all", onClick: onShowAll }}
    />
  );
}

/** The probe behind {@link MyScopeEmptyState}'s "nothing yet": the reader's
 *  own rows, no other filter, one row. */
export const MY_SCOPE_PROBE = { page: 1, pageSize: 1, mineOnly: true } as const;
